import React from 'react';
import { Link } from 'react-router-dom';
import { Quote } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [storyRef, storyVisible] = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/hxzyvphj_IMG_0090.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="about-hero">
          <h1 className="text-4xl md:text-6xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Our Story</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Built on Love, Family and Togetherness</p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-24 px-4 bg-cream-dark" data-testid="story-section">
        <div className="max-w-4xl mx-auto">
          <div className="w-16 h-1 bg-gold mx-auto mb-12"></div>
          
          <div className="bg-white p-16 rounded-lg shadow-luxury mb-12">
            <p className="text-xl text-gray-800 leading-relaxed mb-8 text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, letterSpacing: '0.02em'}}>
              Mama-Mam's Kitchen is more than just a place to eat, it is a space built on love, family and togetherness.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.01em'}}>
              Inspired by the memory of <strong className="text-brown">Marjorie John</strong>, affectionately known as Mama-Mam, this kitchen was created to keep her spirit alive. Her warmth, her cooking and her ability to bring people together remain at the heart of everything we do.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.01em'}}>
              Founded by <strong className="text-brown">Angella</strong>, Mama-Mam's Kitchen is rooted in a long-standing family tradition. Every Sunday, the family would gather around the table, sharing home-cooked food, laughter, stories and moments that truly matter. It was never just about the food, but the feeling of being together.
            </p>
          </div>

          <div className="bg-gold-light p-16 rounded-lg shadow-luxury mb-12 border-l-4 border-gold">
            <p className="text-lg text-gray-700 leading-relaxed mb-8 italic" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
              As life moves forward, moments like these can become rare. Not everyone has the time, space or opportunity to bring loved ones together in the same way.
            </p>
            
            <p className="text-2xl text-brown font-semibold text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.02em'}}>
              That is why Mama-Mam's Kitchen exists.
            </p>
          </div>

          <div className="bg-white p-16 rounded-lg shadow-luxury">
            <p className="text-lg text-gray-700 leading-relaxed mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.01em'}}>
              We have created a place where everyone is welcome, a place where you can sit, eat, connect and feel at home. Whether you come with family, friends or on your own, you are invited to be part of something special.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.01em'}}>
              With soulful, home-style cooking, a warm atmosphere, and touches of <strong className="text-gold">live music and entertainment</strong>, every visit is designed to feel like a Sunday at home.
            </p>
          </div>
        </div>
      </section>

      {/* Logo Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <img 
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_client-web-setup/artifacts/u0r810nb_6587f527-a426-4957-b4cf-1f060757bebd.jpeg" 
            alt="Mama-Mam's Kitchen Logo" 
            className="w-full max-w-2xl mx-auto"
          />
        </div>
      </section>

      {/* Image Section - Real Event Photos */}
      <section className="py-16 px-4 bg-cream-dark">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-brown mb-12 text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Our Family Gatherings</h2>
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="relative overflow-hidden rounded-lg shadow-luxury">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/1giozw59_IMG_0127.jpeg" 
                alt="Multi-generational family at Mama-Mam's" 
                className="w-full h-96 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-luxury">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg" 
                alt="Community gathering at Mama-Mam's" 
                className="w-full h-96 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-luxury max-w-4xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/5jlps656_IMG_0092.jpeg" 
              alt="Elegant venue setting" 
              className="w-full h-96 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 bg-cream-dark">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
          <h2 className="text-5xl font-bold text-brown mb-16" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>What We Stand For</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-lg shadow-luxury-hover transition-all">
              <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
              <h3 className="text-3xl font-bold mb-4 text-gold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Love</h3>
              <p className="text-gray-600 leading-relaxed">Every dish, every moment, infused with the love Mama-Mam taught us.</p>
            </div>
            <div className="bg-white p-10 rounded-lg shadow-luxury-hover transition-all">
              <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
              <h3 className="text-3xl font-bold mb-4 text-gold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Family</h3>
              <p className="text-gray-600 leading-relaxed">Creating space for families to reconnect and new bonds to form.</p>
            </div>
            <div className="bg-white p-10 rounded-lg shadow-luxury-hover transition-all">
              <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
              <h3 className="text-3xl font-bold mb-4 text-gold" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Togetherness</h3>
              <p className="text-gray-600 leading-relaxed">A welcoming home where everyone belongs, no matter who you are.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Angella's Note */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gold-light p-16 rounded-lg shadow-luxury border-2 border-gold relative">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-16 h-1 bg-gold"></div>
            </div>
            <p className="text-2xl text-gray-700 italic leading-relaxed mb-8 text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.01em'}}>
              "<strong>Mama-Mam</strong> showed me that the greatest gift you can give someone is a seat at your table and a plate of food made with love. This kitchen is my way of honouring her memory and sharing that gift with you."
            </p>
            <p className="font-bold text-brown text-3xl text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.03em'}}>— Angella, Founder</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gold text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Become Part of Our Family</h2>
        <p className="text-xl mb-8 opacity-90">Experience the warmth, the love, and the togetherness</p>
        <Link 
          to="/reserve" 
          className="inline-block bg-white text-brown hover:bg-cream font-semibold px-10 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg"
          data-testid="about-cta-button"
        >
          Reserve Your Table →
        </Link>
      </section>
    </div>
  );
};

export default About;
